import React, { useState, MouseEvent } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
  Menu,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Refresh as RefreshIcon,
  Logout as LogoutIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  AccountCircle as AccountCircleIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { User, Organization } from '../types';
import apiService from '../services/api';

interface NavbarProps {
  userRole: string;
  user: User;
  selectedOrg?: Organization | null;
  onLogout: () => void;
  onBackToOrgSelector?: (() => void) | null;
}

const Navbar: React.FC<NavbarProps> = ({ 
  userRole, 
  user, 
  selectedOrg, 
  onLogout, 
  onBackToOrgSelector 
}) => {
  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);

  const handleRefresh = async () => {
    try {
      await apiService.getSalesData();
      console.log('Data refresh triggered');
      window.location.reload();
    } catch (error) {
      console.error('Failed to refresh data:', error);
    }
  };

  return (
    <AppBar 
      position="static" 
      elevation={1}
      sx={{
        backgroundColor: '#2C3E50',
        borderBottom: 'none',
      }}
    >
      <Toolbar sx={{ minHeight: { xs: '56px', sm: '64px' }, py: { xs: 0.5, sm: 0 }, px: { xs: 2, sm: 3 } }}>
        <Box 
          component={Link}
          to="/"
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mr: 4,
            textDecoration: 'none',
            '&:hover': {
              opacity: 0.8,
            },
          }}
        >
          <img 
            src={`${process.env.PUBLIC_URL}/logos/SONIQPulseLogo.png`}
            alt="SONIQ Pulse Logo"
            style={{ 
              height: '45px',
              width: 'auto',
              objectFit: 'contain',
              filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
              cursor: 'pointer',
            }}
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              if (!img.src.includes('NightSuperBlue')) {
                img.src = `${process.env.PUBLIC_URL}/logos/NightSuperBlue.png`;
              } else {
                img.style.display = 'none';
              }
            }}
          />
        </Box>

        {selectedOrg && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
            {onBackToOrgSelector && (
              <IconButton
                onClick={onBackToOrgSelector}
                title="Back to Organization Selector"
                size="small"
                sx={{
                  color: '#4B88A2',
                  '&:hover': {
                    backgroundColor: 'rgba(75, 136, 162, 0.1)',
                  },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            <Chip
              icon={<BusinessIcon />}
              label={selectedOrg.name}
              size="small"
              sx={{
                backgroundColor: '#4B88A2',
                color: '#F8F9FA',
                fontWeight: 500,
                '& .MuiChip-label': {
                  color: '#F8F9FA',
                },
                '& .MuiChip-icon': {
                  color: '#F8F9FA',
                },
              }}
            />
          </Box>
        )}

        <Box sx={{ 
          display: { xs: 'none', md: 'flex' }, 
          alignItems: 'center', 
          gap: 1,
          flexGrow: 1
        }}>
          <Button
            component={Link}
            to="/"
            startIcon={<DashboardIcon />}
            sx={{
              color: '#ffffff',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
              },
              fontWeight: 600,
            }}
          >
            Home
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
          <IconButton
            onClick={handleRefresh}
            title="Refresh Data"
            size="small"
            sx={{
              color: '#ffffff',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <RefreshIcon />
          </IconButton>

          {user && (
            <>
              <Button
                onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                startIcon={<AccountCircleIcon />}
                sx={{
                  color: '#ffffff',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  display: { xs: 'none', sm: 'flex' },
                }}
              >
                {user.username}
              </Button>
              
              <IconButton
                onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                size="small"
                sx={{
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  display: { xs: 'flex', sm: 'none' },
                }}
              >
                <AccountCircleIcon />
              </IconButton>

              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={() => setUserMenuAnchor(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <Box sx={{ px: 2, py: 1.5, minWidth: 200 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {user.username}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Role: {userRole.charAt(0).toUpperCase() + userRole.slice(1)}
                  </Typography>
                  {selectedOrg && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {selectedOrg.name}
                    </Typography>
                  )}
                </Box>
                <Divider />
                <MenuItem onClick={() => { setUserMenuAnchor(null); handleRefresh(); }}>
                  <ListItemIcon>
                    <RefreshIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Refresh Data</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => { setUserMenuAnchor(null); onLogout(); }}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
